import React from 'react';
import { X } from 'lucide-react';

const PromptDetailsModal = ({ selectedPrompt, closePromptDetails }) => {
  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value) => {

    return new Intl.NumberFormat('en-US').format(value);
  };

  // Format time (ms to seconds)
  const formatTime = (value) => {
    if (value === undefined || value === null) {
      return "0.00s";
    }
    return `${value.toFixed(2)}s`;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={(e) => {
        // Close when clicking outside the modal
        if (e.target === e.currentTarget) {
          closePromptDetails();
        }
      }}
    >
      <div
        className="bg-white rounded-lg p-4 overflow-y-auto shadow-xl transform transition-all animate-modalFade"
        style={{
          width: '550px',    // Medium width
          maxWidth: '90vw',  // 90% of viewport width
          maxHeight: '80vh', // 80% of viewport height
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3 sticky top-0 bg-white pb-2 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800">Prompt Details</h2>
          <button
            onClick={closePromptDetails}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-primary">ID</p>
              <p className="font-medium text-sm truncate">{selectedPrompt.id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-primary">Timestamp</p>
              <p className="font-medium text-sm truncate">{new Date(selectedPrompt.timestamp).toLocaleString()}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-primary">Customer</p>
              <p className="font-medium text-sm truncate">{selectedPrompt.customerId}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-primary">Endpoint</p>
              <p className="font-medium text-sm truncate">{selectedPrompt.endpoint}</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-primary">Model</p>
            <p className="font-medium text-sm truncate">{selectedPrompt.model}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-primary mb-1">Prompt</p>
            <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-32 overflow-y-auto font-mono text-sm whitespace-pre-wrap">
              {selectedPrompt.prompt}
            </div>
          </div>
          
          {/* Show SQL query if origin function is SQL_Chat.generate_sql */}
          {selectedPrompt.endpoint === 'SQL_chat.generate_sql' && selectedPrompt.category && (
            <div>
              <p className="text-sm font-medium text-primary mb-1">Generated SQL</p>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-48 overflow-y-auto font-mono text-sm whitespace-pre-wrap">
                {(() => {
                  try {
                    // Try to parse the response as JSON to extract the query
                    const responseObj = JSON.parse(selectedPrompt.category);
                    return responseObj.query || 'No SQL query found';
                  } catch (e) {
                    // If parsing fails, check if the response contains a query field directly
                    if (typeof selectedPrompt.category === 'string' && selectedPrompt.category.includes('"query"')) {
                      const match = selectedPrompt.category.match(/"query"\s*:\s*"([^"]*)"/);
                      return match ? match[1].replace(/\\n/g, '\n') : selectedPrompt.category;
                    }
                    return selectedPrompt.category;
                  }
                })()}
              </div>
            </div>
          )}
          
          {selectedPrompt.endpoint !== 'SQL_chat.generate_sql' && selectedPrompt.category && (
            <div>
              <p className="text-sm font-medium text-primary mb-1">Response</p>
              <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-48 overflow-y-auto font-mono text-sm whitespace-pre-wrap">
                {selectedPrompt.category}
              </div>
            </div>
          )}
          
          <div>
            <p className="text-sm font-medium text-primary mb-1">Performance Metrics</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 p-2 rounded text-center">
                <p className="text-sm font-medium text-primary">Input Tokens</p>
                <p className="font-medium">{formatNumber(selectedPrompt.inputTokens)}</p>
              </div>
              <div className="bg-gray-50 p-2 rounded text-center">
                <p className="text-sm font-medium text-primary">Output Tokens</p>
                <p className="font-medium">{formatNumber(selectedPrompt.outputTokens)}</p>
              </div>
              <div className="bg-gray-50 p-2 rounded text-center">
                <p className="text-sm font-medium text-primary">Total Tokens</p>
                <p className="font-medium">{formatNumber(selectedPrompt.totalTokens)}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 p-2 rounded text-center">
              <p className="text-sm font-medium text-primary">Cost</p>
              <p className="font-medium">{formatCurrency(selectedPrompt.cost)}</p>
            </div>
            <div className="bg-gray-50 p-2 rounded text-center">
              <p className="text-sm font-medium text-primary">Latency</p>
              <p className="font-medium">{formatTime(selectedPrompt.latency)}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-2 border-t border-gray-100 flex justify-end">
          <button
            className="bg-primary text-white px-4 py-1.5 rounded text-sm hover:bg-primaryDark"
            onClick={closePromptDetails}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptDetailsModal;